import type { SwissCategory } from '../types'

// Заголовок Swiss-категории генерируется на фронте по `wins[]`/`losses[]` —
// это решение из docs/pickem/docs/technical/03-api-basic.md:
// > чтобы не локализовать и не хранить дубликаты в БД.
//
// Алгоритм:
//   wins=[3], losses=[0]            → "3-0"      (одиночный исход)
//   wins=[3], losses=[1,2]          → "3-1, 3-2" (несколько losses)
//   wins=[0,1,2], losses=[3]        → "0-3, 1-3, 2-3"  (несколько wins)
//   wins=[3], losses=[]             → "Победители" (open-ended на любое losses)
//   wins=[], losses=[3]             → "Выбывшие" (open-ended на любое wins)

const cartesian = (wins: number[], losses: number[]): string[] => {
  const pairs: string[] = []
  for (const w of wins) {
    for (const l of losses) {
      pairs.push(`${w}-${l}`)
    }
  }
  return pairs
}

export const swissCategoryLabel = (category: SwissCategory): string => {
  if (category.wins.length === 0 && category.losses.length === 0) {
    return ''
  }
  if (category.wins.length === 0) {
    return 'Выбывшие'
  }
  if (category.losses.length === 0) {
    return 'Победители'
  }
  return cartesian(category.wins, category.losses).join(', ')
}

// Компактный заголовок для мобильной верстки: одну строку из wins/losses
// заменяем смысловой формулировкой, чтобы не перегружать карточку.
export const swissCategoryCompactLabel = (category: SwissCategory): string => {
  if (category.wins.length === 0) return 'Выбыли'
  if (category.losses.length === 0) return 'Прошли дальше'
  // По бизнесу «3-0» = quickly advance, «3-3»-подобные = середина.
  // Пока оставим точный label — мобила тоже принимает его.
  return swissCategoryLabel(category)
}

// Цветовая семантика категории. Используем наши CSS-переменные:
//   accent-2  — зелёный (победители / 3-0)
//   text-2    — нейтральный (mid)
//   error     — красный (выбывшие)
export type SwissCategoryColor = 'success' | 'neutral' | 'danger'

export const swissCategoryColor = (
  category: SwissCategory
): SwissCategoryColor => {
  // Только wins, без losses → «прошли дальше» = success.
  if (category.losses.length === 0) return 'success'
  // Только losses → «выбыли» = danger.
  if (category.wins.length === 0) return 'danger'
  // Минимум одного 0 в losses при 3 в wins → 3-0, успех.
  const isCleanWin =
    category.wins.length === 1 &&
    category.wins[0] === 3 &&
    category.losses.includes(0)
  if (isCleanWin) return 'success'
  // 0 в wins при максимуме в losses → провал.
  const isCleanLoss = category.losses.length === 1 && category.wins.includes(0)
  if (isCleanLoss) return 'danger'
  return 'neutral'
}

// Человеческое описание для подзаголовка карточки категории. Берём из
// макета «1 - Pickam - активный день»: «Выбери команды, набравшие 3 победы»
// для 3-0; «Выбери команды без побед» для 0-3; «Выбери команды, которые
// пройдут дальше» для остального.
export interface SwissCategoryHintParts {
  before: string
  highlight: string
}

export const swissCategoryHintParts = (
  category: SwissCategory,
  isResultView: boolean
): SwissCategoryHintParts => {
  const color = swissCategoryColor(category)
  if (color === 'success') {
    if (isResultView) {
      return { before: 'Команды, набравшие ', highlight: '3 победы' }
    }
    return { before: 'Выбери команды, набравшие ', highlight: '3 победы' }
  }
  if (color === 'danger') {
    if (isResultView) {
      return { before: 'Команды ', highlight: 'без побед' }
    }
    return { before: 'Выбери команды ', highlight: 'без побед' }
  }

  if (isResultView) {
    return { before: 'Команды, которые ', highlight: 'прошли дальше' }
  }
  return { before: 'Выбери команды, которые ', highlight: 'пройдут дальше' }
}
